import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enAU from 'date-fns/locale/en-AU';
import AddressAutocomplete, { AddressValues } from '../AddressAutocomplete';

function formatAddressFromValues(v: AddressValues): string {
  const line1 = [v.address_line1, v.address_line2].filter(Boolean).join(', ');
  const loc = [v.address_suburb, v.address_state, v.address_postcode].filter(Boolean).join(' ');
  const parts = [line1, loc, v.address_country].filter(Boolean);
  return parts.join(', ') || (v.address_formatted ?? '');
}

interface CustomerDetailsProps {
  customerType: 'residential' | 'business';
  onCustomerTypeChange: (type: 'residential' | 'business') => void;
  onDataChange: (data: any) => void;
  /** Prefill from service address entered on step 1 (plan selection). Shown as "Address" – may differ from that value. */
  initialServiceAddress?: string;
  /** When true, show inline error state on any required field that is empty/invalid. */
  showErrors?: boolean;
  /** Previously entered values, restored when the step remounts (e.g. after Back). */
  initialData?: any;
}

function CustomerDetails({
  customerType,
  onCustomerTypeChange,
  onDataChange,
  initialServiceAddress = '',
  showErrors = false,
  initialData,
}: CustomerDetailsProps) {
  const [formData, setFormData] = useState<Record<string, any>>({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    dateOfBirth: null as Date | null,
    serviceAddress: initialServiceAddress,
    billingAddress: '',
    organisationName: '',
    abnAcn: '',
    ...(initialData || {}),
  });
  const [addressValues, setAddressValues] = useState<AddressValues>({});

  // Pre-fill address from step 1 when we have it (e.g. user navigated to this step)
  useEffect(() => {
    if (initialServiceAddress?.trim()) {
      setFormData((prev) =>
        prev.serviceAddress ? prev : { ...prev, serviceAddress: initialServiceAddress.trim() }
      );
    }
  }, [initialServiceAddress]);

  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressStructuredChange = (v: AddressValues) => {
    setAddressValues(v);
    const formatted = formatAddressFromValues(v);
    if (formatted) handleChange('serviceAddress', formatted);
  };

  const handleAddressLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange('serviceAddress', e.target.value);
  };

  const emailError = showErrors
    ? !formData.email.trim()
      ? 'Email is required'
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ? 'Please enter a valid email address'
        : undefined
    : undefined;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Customer Details
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Customer Type</FormLabel>
        <RadioGroup
          row
          value={customerType}
          onChange={(e) => onCustomerTypeChange(e.target.value as 'residential' | 'business')}
        >
          <FormControlLabel value="residential" control={<Radio />} label="Residential" />
          <FormControlLabel value="business" control={<Radio />} label="Business" />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={3}>
        {customerType === 'business' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Organisation Name"
                value={formData.organisationName}
                onChange={(e) => handleChange('organisationName', e.target.value)}
                error={showErrors && !formData.organisationName.trim()}
                helperText={showErrors && !formData.organisationName.trim() ? 'Organisation name is required' : undefined}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="ABN/ACN"
                value={formData.abnAcn}
                onChange={(e) => handleChange('abnAcn', e.target.value)}
                error={showErrors && !formData.abnAcn.trim()}
                helperText={showErrors && !formData.abnAcn.trim() ? 'ABN/ACN is required' : undefined}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={showErrors && !formData.firstName.trim()}
            helperText={showErrors && !formData.firstName.trim() ? 'First name is required' : undefined}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={showErrors && !formData.lastName.trim()}
            helperText={showErrors && !formData.lastName.trim() ? 'Last name is required' : undefined}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="email"
            label="Email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!emailError}
            helperText={emailError ?? 'Accounts and correspondence will be sent to this email address.'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Mobile Number"
            value={formData.mobileNumber}
            onChange={(e) => handleChange('mobileNumber', e.target.value)}
            placeholder="e.g., 0412345678"
            error={showErrors && !formData.mobileNumber.trim()}
            helperText={showErrors && !formData.mobileNumber.trim() ? 'Mobile number is required' : undefined}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
            <DatePicker
              label="Date of Birth *"
              format="dd/MM/yyyy"
              value={formData.dateOfBirth}
              onChange={(date) => handleChange('dateOfBirth', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: showErrors && !formData.dateOfBirth,
                  helperText: showErrors && !formData.dateOfBirth ? 'Date of birth is required' : undefined,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pre-filled from the address you entered on the first page. Expand below if you need to change or validate it.
          </Typography>
          <TextField
            fullWidth
            required
            label="Address"
            multiline
            minRows={1}
            maxRows={3}
            value={formData.serviceAddress}
            onChange={handleAddressLineChange}
            placeholder="Full address"
            sx={{ mb: 2 }}
            error={showErrors && !formData.serviceAddress.trim()}
            helperText={showErrors && !formData.serviceAddress.trim() ? 'Address is required' : undefined}
          />
          <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
              <Typography variant="body2" color="text.secondary">
                Change or validate address (Google)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Search with Google (Australia) or use the fields below. The address above will update when you select a result.
              </Typography>
              <AddressAutocomplete
                value={addressValues}
                onChange={handleAddressStructuredChange}
                line1Label="Street address"
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Tip:</strong> This address may differ from the service address you entered on the first page. Use this field for the address you want on your account and correspondence.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Billing Address (if different)"
            multiline
            rows={3}
            value={formData.billingAddress}
            onChange={(e) => handleChange('billingAddress', e.target.value)}
            placeholder="Leave blank if same as address above"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default CustomerDetails;

